/* eslint-disable  */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input, Typography } from 'tfc-components';

import { CameraIcon, PaperclipIcon } from '@phosphor-icons/react';
import Icon from 'components/atoms/Icon';
import { useToast } from 'components/molecules/Toast';
import Modal from 'components/organisms/Modal';
import usePermission from 'hooks/usePermission';
import useUploadFileService from 'hooks/useUploadFileService';
import { useTranslation } from 'react-i18next';
import { allowedExtensions, allowedImageExtensions, MAX_SIZE_UPLOAD } from 'utils/constants';
import mapModifiers, { compressImageFile, formatBytes } from 'utils/functions';

interface Props {
  // method: UseFormReturn<FormReturnTypes>
  handleChooseFile(file: UploadFileType): void;
  handleError?: (message: string) => void;
  bigSize?: boolean;
  conversationCode: string;
  handleChunkLoading?: (isLoading: boolean) => void;
  disabled?: boolean;
  fileLengthValues: number;
  imgLengthValues: number;
  totalFileLength?: number;
  imgLimit?: number;
  fileLimit?: number;
  type: 'uploadFiles' | 'camera' | 'any';
  displayType?: 'default' | 'text';
  onCamera?: () => void;
  onClick?: () => void;
  filesState?: UploadFileType[];
}

const UploadMultipleFiles: React.FC<Props> = ({
  handleChooseFile,
  conversationCode,
  bigSize,
  handleError,
  handleChunkLoading,
  disabled,
  imgLimit,
  fileLimit,
  fileLengthValues,
  imgLengthValues,
  type,
  onCamera,
  displayType = 'default',
  onClick,
  totalFileLength,
  filesState,
}) => {
  const [permission, setPermission] = usePermission({ type: 'camera' });
  const { showToast } = useToast();
  const fileRef = useRef<File | undefined>(undefined);
  const { t } = useTranslation();
  const [cameraPermissionShow, setCameraPermissionShow] = useState(permission === 'prompt');
  const [cameraPermissionAlertShow, setCameraPermissionAlertShow] = useState(false);
  const [hasCamera, setHasCamera] = useState(true);

  const { uploader } = useUploadFileService({
    conversationCode,
    handleChooseFile,
    handleChunkLoading,
    allowedExtensions: [...allowedExtensions, ...allowedImageExtensions],
  });

  const handleClick = () => {
    if (permission === 'granted') {
      onCamera?.();
    } else if (permission === 'denied' && type === 'camera') {
      setCameraPermissionAlertShow(true);
    } else {
      setCameraPermissionShow(true);
    }
  };

  const handleAllow = async () => {
    setCameraPermissionShow(false);
    try {
      await navigator.mediaDevices.getUserMedia({ video: true });
      setPermission('granted');
      onCamera?.();
    } catch {
      setPermission('denied');
      setCameraPermissionAlertShow(true);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e?.target?.files;
    if (!files || files.length === 0) return;

    let uploadedFiles = 0;
    let uploadedImgs = 0;
    Array.from(files || []).forEach((file) => {
      if (file.type.includes('image')) {
        uploadedImgs++;
      } else {
        uploadedFiles++;
      }
    });

    if (
      imgLimit &&
      fileLimit &&
      (uploadedImgs + imgLengthValues > imgLimit || uploadedFiles + fileLengthValues > fileLimit)
    ) {
      showToast({
        message: `Bạn chỉ có thể tải lên tối đa ${imgLimit} ảnh và ${fileLimit} tệp.`,
        show: true,
        type: 'error',
      });
      return;
    }
    // Process each file in the selection
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const uid = `${file.name}-${file.size}`;
      const extension = file.name.split('.')?.pop()?.toLowerCase();
      if (extension && ![...allowedImageExtensions, ...allowedExtensions].includes(extension)) {
        handleChooseFile({
          file,
          id: uid,
          message: t('chatbot.fileNotSupport'),
          status: 'error',
        });
        // if file is image but not in allowedImageExtensions, show 'Định dạng ảnh không hợp lệ. Vui lòng chọn ảnh JPG hoặc PNG.'
        if (file.type.startsWith('image/')) {
          showToast({
            message: t('chatbot.invalidImageType'),
            show: true,
            type: 'error',
          });
        }

        if (!file.type.startsWith('image/')) {
          showToast({
            message: t('chatbot.invalidFileType'),
            show: true,
            type: 'error',
          });
        }
        return;
      }
      if ((file.size || 0) > MAX_SIZE_UPLOAD) {
        handleChooseFile({
          file,
          id: uid,
          message: t("chatbot.fileTooLarge", { maxSize: formatBytes(MAX_SIZE_UPLOAD) }),
          status: 'error',
        });
        if (file.type.startsWith('image/')) {
          showToast({
            message: t("chatbot.imageTooLarge", { maxSize: formatBytes(MAX_SIZE_UPLOAD) }),
            show: true,
            type: 'error',
          });
        } else {
          showToast({
            message: t("chatbot.limitFile", { maxSize: formatBytes(MAX_SIZE_UPLOAD) }),
            show: true,
            type: 'error',
          });
        }
        return;
      }

      if (file?.type.startsWith('image/')) {
        try {
          const compressedFile = (await compressImageFile(file, bigSize)) as File;
          fileRef.current = compressedFile;
          uploader.addFiles(compressedFile);
          uploader.uploadStoredFiles();
        } catch (e) {
          console.log('Upload error for image:', file.name, e);
          if (handleError) {
            handleError(`Failed to upload image: ${file.name}`);
          }
        }
      } else {
        try {
          fileRef.current = file;
          uploader.addFiles(file);
          uploader.uploadStoredFiles();
        } catch (e) {
          console.log('Upload error for file:', file.name, e);
          if (handleError) {
            handleError(`Failed to upload file: ${file.name}`);
          }
        }
      }
    }
    e.target.value = "";
  };

  // 🔹 Kiểm tra xem thiết bị có camera hay không
  useEffect(() => {
    if (type === 'camera') {
      navigator.mediaDevices.enumerateDevices().then((devices) => {
        const videoDevices = devices.filter((device) => device.kind === 'videoinput');
        if (videoDevices.length === 0) {
          setHasCamera(false);
        }
      });
    }
  }, [type]);

  // Determine if button should be disabled
  const isFileUploading = useMemo(() => filesState?.some((file) => file.status === 'loading'), [filesState]);
  const isButtonDisabled = disabled || isFileUploading;

  useEffect(() => {
    if (permission === 'denied' && cameraPermissionAlertShow) {
      showToast({
        message: t('chatbot.cameraRequest'),
        show: true,
        type: 'error',
      });
    }
  }, [permission, cameraPermissionAlertShow]);

  return (
    <div className={mapModifiers('o-chatBotFooter_actions', type)}>
      {displayType === 'default' ? (
        <button
          className={mapModifiers('o-chatBotFooter_actions_button')}
          // className={mapModifiers('o-chatBotFooter_actions_button', Number(totalFileLength) > 0 && 'active')}
          disabled={isButtonDisabled}
          onClick={onClick}
        >
          <label role="button">
            {type === 'camera' ? (
              <>
                {hasCamera ? (
                  <div onClick={() => {
                    if (permission === 'denied') {
                      showToast({
                        message: t('chatbot.cameraPermissionDenied'),
                        show: true,
                        type: 'error',
                      });
                    } else {
                      handleClick?.()
                    }
                  }}>
                    <CameraIcon size={20} color="#1F1F1F" />
                  </div>
                ) : (
                  <>
                    <CameraIcon size={20} color="#1F1F1F" />
                    <Input
                      disabled={isButtonDisabled}
                      extendClasses="m-uploadPhoto_input"
                      type="file"
                      onChange={handleUpload}
                      accept={allowedImageExtensions.map((item) => `.${item}`).join(', ')}
                      multiple
                    />
                  </>
                )}
              </>
            ) : (
              <>
                {/* <Attached
                  className={mapModifiers('o-chatBotFooter_actions_attached')}
                /> */}
                <PaperclipIcon size={20} color="#1F1F1F" />
                {/* {Number(totalFileLength) <= 0 && ( */}
                <Input
                  disabled={isButtonDisabled}
                  type="file"
                  onChange={handleUpload}
                  extendClasses="o-chatBotFooter_actions_inputFile"
                  multiple
                  accept={[...allowedExtensions, ...allowedImageExtensions].map((item) => `.${item}`).join(', ')}
                />
                {/* )} */}
              </>
            )}
          </label>
        </button>
      ) : (
        <button
          disabled={isButtonDisabled}
          className={`o-chatBotFooter_area_uploadContent_right_button ${isFileUploading ? 'disabled' : ''}`}
        >
          <label role="button" style={{ display: 'flex', alignItems: 'center' }}>
            <Icon iconName="whitePlus" size="20x20" />
            <Typography.Text>Add</Typography.Text>
            <Input
              disabled={isButtonDisabled}
              type="file"
              onChange={handleUpload}
              extendClasses="o-chatBotFooter_actions_inputFile"
              multiple
              accept={[...allowedExtensions, ...allowedImageExtensions].map((item) => `.${item}`).join(', ')}
            />
          </label>
        </button>
      )}

      <Modal
        isOpen={permission !== 'granted' && cameraPermissionShow}
        cancelButton={{
          handleClick: () => {
            setCameraPermissionShow(false);
          },
          text: t('chatbot.cancel'),
        }}
        okButton={{
          handleClick: handleAllow,
          text: t('chatbot.accept'),
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Icon iconName="cameraPermission" size="60" />
          <Typography.Text textStyle="center" extendClasses="t-chatting_modalWarning_description">
            {t('chatbot.permissionDenined')}
          </Typography.Text>
        </div>
      </Modal>
    </div>
  );
};
export default UploadMultipleFiles;
